import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  TableBody,
  TableRow,
  useTheme,
  TableContainer,
} from '@mui/material';
import Form from 'components/Form';
import {
  StyledTableCellTwo,
  StyledTableCellWithoutLeftTwo,
  StyledTableCellWithoutLeftRightTwo,
} from 'routes/Main/style';
import SectionTitle from '../../components/SectionTitle';

import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

const radioId5 = ['no38', 'no39', 'no40', 'no41', 'no42', 'no43', 'no44'];

const 영역5 = [
  {
    id: 1,
    ko: '38. 침상목욕',
    info: ['직원이 부분 혹은 전신목욕을 해준 경우 해당된다.'],
    desc: ['해당 없음', '4점'],
  },
  {
    id: 2,
    ko: '39. 침상세발',
    info: ['직원이 침상에서 세발해준 경우에만 해당된다.'],
    desc: ['해당 없음', '1점'],
  },
  {
    id: 3,
    ko: '40. 구강간호',
    info: [
      '소독볼을 이용하는 경우 뿐 아니라 가글링, 양치질을 하도록 도운 경우도 모두 해당된다.',
    ],
    desc: ['해당 없음', '1점 : 1~2회', '2점 : 3회 이상'],
  },
  {
    id: 4,
    ko: '41. 회음부간호',
    info: ['소독액 사용 뿐 아니라 물수건으로 세척한 경우도 모두 해당된다.'],
    desc: ['해당 없음 ', '1회당 1점 : '],
  },
  {
    id: 5,
    ko: '42. 린넨교환',
    info: [
      '침상목욕 시 시행한 린넨교환을 포함하여 incontinent, vomiting, diaphoretic care 등을 위해 직원 2인 이상이 린넨 교환을 하는 경우 해당된다.',
    ],
    desc: ['해당 없음  ', '1회당 1.5점 :'],
  },
  {
    id: 6,
    ko: '43. 접촉격리 및 역격리 간호',
    info: [
      '환자를 격리(코호트 포함)하고, 매 처치 시마다 gowning, gloving 등을 하는 경우에만 해당된다.',
    ],
    desc: ['해당 없음', '8점'],
  },
  {
    id: 7,
    ko: '44. 비말 및 공기격리 간호',
    info: [
      '환자를 격리(코호트 포함)하고, 매 처치 시마다 masking (surgical mask, N95 mask)을 하는 경우에만 해당된다.',
    ],
    desc: ['해당 없음', '4점'],
  },
];

const radioId6 = ['no45', 'no46', 'no47', 'no48', 'no49'];

const 영역6 = [
  {
    id: 1,
    ko: '45. 경관영양(일회성-bolus)',
    info: [
      '비위관, 위루 영양을 위해 튜브 위치 사정, 영양 주입액 준비/연결/속도 조절/제거/주입관 세척, 기록을 포함한다.',
    ],
    desc: ['해당 없음', '2점: 1~3회', '6점: 4~6회', '10점: 7~12회'],
  },
  {
    id: 2,
    ko: '46. 경관영양(지속성-continuous)',
    info: [
      '튜브위치 사정, 주입액의 연결, 주입속도 조절, 주입량 기록을 포함한다.',
    ],
    desc: ['해당 없음', '3점'],
  },
  {
    id: 3,
    ko: '47. 성인 식사보조(spoon feed)',
    info: [
      '직원이 spoon feed를 전적으로 도와주거나, 환자가 직접 spoon feed을 시행한 경우 모두 해당되며, 식사를 돕고 준비해주는 과정이 포함된다.',
    ],
    desc: ['해당 없음', '6점 : 3회 이상'],
  },
  {
    id: 4,
    ko: '48. 아동 식사보조(spoon feed)',
    info: [
      '직원이 spoon feed를 전적으로 도와주거나, 환자가 직접 spoon feed을 시행한 경우 모두 해당되며, 식사를 돕고 준비해주는 과정이 포함된다.',
    ],
    desc: ['해당 없음', '15점: 3회 이상'],
  },
  {
    id: 5,
    ko: '49. 신생아/영아 젖병수유',
    info: [
      '매 병당 횟수 기록. 환아를 안고 feeding 후 트림을 시키고, 침상에 눕히는 것을 포함한다.',
    ],
    desc: ['해당 없음 ', '1회당 4점 :'],
  },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  sum5: number;
  setSum5: (sum5: number) => void;

  perineum: number;
  setPerineum: (perineum: number) => void;

  linen: number;
  setLinen: (linen: number) => void;

  sum6: number;
  setSum6: (sum6: number) => void;

  lactation: number;
  setLactation: (lactation: number) => void;
}

const KPCSCContents3 = (props: Props) => {
  const { palette } = useTheme();

  const {
    disabled,
    setValue,
    getValues,
    setSum5,
    setPerineum,
    setLinen,
    setSum6,
    setLactation,
    register,
  } = props;

  const calculateSumValue5 = () => {
    setSum5(
      radioId5.reduce((acc, cur) => {
        if (cur === 'no41' || cur === 'no42') {
          const value = Number(getValues(cur));
          return value ? acc + value - 1 : acc;
        }
        // 38, 44번
        else if (cur === 'no38' || cur === 'no44') {
          const value = Number(getValues(cur)) * 4;
          return value ? acc + value : acc;
        } // 38, 44번
        else if (cur === 'no43') {
          const value = Number(getValues(cur)) * 8;
          return value ? acc + value : acc;
        }

        const value = Number(getValues(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  const handleChange5 = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    calculateSumValue5();
  };

  const [disablePerineum, setDisablePerineum] = useState(false);

  const handleChange5Perineum_none = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no41_1', 0);
    setPerineum(0);
    setDisablePerineum(true);
    calculateSumValue5();
  };

  const handleChange5Perineum_One = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no41_1', 1);
    setPerineum(1);
    setDisablePerineum(false);
    calculateSumValue5();
  };

  const [disableLinen, setDisableLinen] = useState(false);

  const handleChange5Linen_none = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no42_1', 0);
    setLinen(0);
    setDisableLinen(true);
    calculateSumValue5();
  };

  const handleChange5Linen_One = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no42_1', 1);
    setLinen(1.5);
    setDisableLinen(false);
    calculateSumValue5();
  };

  const calculateSumValue6 = () => {
    setSum6(
      radioId6.reduce((acc, cur) => {
        if (cur === 'no49') {
          const value = Number(getValues(cur));
          return value ? acc + value - 1 : acc;
        }
        // 45번
        else if (cur === 'no45') {
          if (Number(getValues(cur)) === 1) {
            const value = Number(getValues(cur)) * 2;
            return value ? acc + value : acc;
          } else if (Number(getValues(cur)) === 2) {
            const value = Number(getValues(cur)) * 3;
            return value ? acc + value : acc;
          } else if (Number(getValues(cur)) === 3) {
            const value = Number(getValues(cur)) * 3 + 1;
            return value ? acc + value : acc;
          }
        }
        // 46번
        else if (cur === 'no46') {
          const value = Number(getValues(cur)) * 3;
          return value ? acc + value : acc;
        }
        // 47번
        else if (cur === 'no47') {
          const value = Number(getValues(cur)) * 6;
          return value ? acc + value : acc;
        }
        // 48번
        else if (cur === 'no48') {
          const value = Number(getValues(cur)) * 15;
          return value ? acc + value : acc;
        }
        const value = Number(getValues(cur));
        return value ? acc + value : acc;
      }, 0)
    );
  };

  const handleChange6 = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    calculateSumValue6();
  };

  const [disableLactation, setDisableLactation] = useState(false);

  const handleChange6Lactation_none = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no49_1', 0);
    setLactation(0);
    setDisableLactation(true);
    calculateSumValue6();
  };

  const handleChange6Lactation_One = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue(e.target.name, e.target.value);
    setValue('no49_1', 1);
    setLactation(4);
    setDisableLactation(false);
    calculateSumValue6();
  };

  useEffect(() => {
    calculateSumValue5();
    calculateSumValue6();
  }, []);

  return (
    <>
      <SectionTitle title="영역 : 위생간호 및 감염관리" />
      <Box sx={{ width: '98%', margin: '20px' }}>
        <TableContainer sx={{ marginTop: '20px', marginBottom: '30px' }}>
          <TableBody>
            {영역5.map(
              (
                content: {
                  id: number;
                  ko: string;
                  info: string[];
                  desc: string[];
                },
                i
              ) => (
                <TableRow>
                  <StyledTableCellTwo
                    sx={{
                      padding: '16px',
                      width: '300px',
                      whiteSpace: 'pre-wrap',
                      verticalAlign: 'top',
                      borderBottom: 영역5.length - 1 === i ? 'none' : '',
                    }}
                  >
                    {content.ko}
                  </StyledTableCellTwo>
                  <StyledTableCellWithoutLeftTwo
                    sx={{
                      borderBottom: 영역5.length - 1 === i ? 'none' : '',
                    }}
                  >
                    <RadioGroup
                      sx={{
                        paddingTop: content.info.length > 1 ? '55px' : '28px',
                      }}
                      name={radioId5[content.id - 1]}
                      defaultValue={Number(getValues(radioId5[content.id - 1]))}
                    >
                      {content.desc.map((point, i) => {
                        if (point === '해당 없음 ') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no41'}
                                  value={i}
                                  onChange={handleChange5Perineum_none}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else if (point === '1회당 1점 : ') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no41'}
                                  value={i}
                                  onChange={handleChange5Perineum_One}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else if (point === '해당 없음  ') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no42'}
                                  value={i}
                                  onChange={handleChange5Linen_none}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else if (point === '1회당 1.5점 :') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no42'}
                                  value={i}
                                  onChange={handleChange5Linen_One}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={radioId5[content.id - 1]}
                                  value={i}
                                  onChange={handleChange5}
                                />
                              </Box>
                            </TableRow>
                          );
                      })}
                    </RadioGroup>
                  </StyledTableCellWithoutLeftTwo>
                  <StyledTableCellWithoutLeftRightTwo
                    sx={{
                      borderBottom: 영역5.length - 1 === i ? 'none' : '',
                    }}
                  >
                    <Box>
                      {content.info.includes('') ? (
                        <TableRow
                          sx={{
                            height: '16px',
                          }}
                        >
                          <Box sx={{ minWidth: '500px' }}> </Box>
                        </TableRow>
                      ) : content.info.length > 1 ? (
                        content.info.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '23px',
                                height: '4px',
                              }}
                            >
                              <Box
                                sx={{ minWidth: '65vw', marginLeft: '-28px' }}
                              >
                                ・ {item}
                              </Box>
                            </TableRow>
                          );
                        })
                      ) : (
                        content.info.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                height: '8.5px',
                              }}
                            >
                              <Box
                                sx={{ minWidth: '65vw', marginLeft: '-28px' }}
                              >
                                ・ {item}
                              </Box>
                            </TableRow>
                          );
                        })
                      )}
                      {content.desc.map((v, i) => {
                        if (v === '1회당 1점 : ') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '42px',
                              }}
                            >
                              <Box sx={{ minWidth: '500px' }}>
                                {v}
                                <Form.MuiTextField
                                  required={false}
                                  type="number"
                                  textAlign="right"
                                  disabled={disablePerineum ? true : disabled}
                                  sx={{ width: '200px', marginLeft: '18px' }}
                                  InputProps={{
                                    ...Form.adornment('', '회'),
                                  }}
                                  {...register('no41_1', {
                                    onChange: e => {
                                      setValue(
                                        'no41_1',
                                        Number(e.target.value)
                                      );
                                      setPerineum(
                                        Math.floor(getValues('no41_1'))
                                      );
                                    },
                                  })}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else if (v === '1회당 1.5점 :') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '42px',
                              }}
                            >
                              <Box sx={{ minWidth: '500px' }}>
                                {v}
                                <Form.MuiTextField
                                  required={false}
                                  type="number"
                                  textAlign="right"
                                  disabled={disableLinen ? true : disabled}
                                  sx={{ width: '200px', marginLeft: '10px' }}
                                  InputProps={{
                                    ...Form.adornment('', '회'),
                                  }}
                                  {...register('no42_1', {
                                    onChange: e => {
                                      setValue(
                                        'no42_1',
                                        Number(e.target.value)
                                      );
                                      setLinen(
                                        Math.floor(getValues('no42_1')) * 1.5
                                      );
                                    },
                                  })}
                                />
                              </Box>
                            </TableRow>
                          );
                        }
                        return (
                          <TableRow
                            sx={{
                              lineHeight: '42px',
                            }}
                          >
                            <Box sx={{ minWidth: '500px' }}>{v}</Box>
                          </TableRow>
                        );
                      })}
                    </Box>
                  </StyledTableCellWithoutLeftRightTwo>
                </TableRow>
              )
            )}
          </TableBody>
        </TableContainer>

        <SectionTitle title="영역 : 영양" />
        <TableContainer sx={{ marginTop: '40px', marginBottom: '-30px' }}>
          <TableBody>
            {영역6.map(
              (
                content: {
                  id: number;
                  ko: string;
                  info: string[];
                  desc: string[];
                },
                i
              ) => (
                <TableRow>
                  <StyledTableCellTwo
                    sx={{
                      padding: '16px',
                      width: '300px',
                      whiteSpace: 'pre-wrap',
                      verticalAlign: 'top',
                      borderBottom: 영역6.length - 1 === i ? 'none' : '',
                    }}
                  >
                    {content.ko}
                  </StyledTableCellTwo>
                  <StyledTableCellWithoutLeftTwo
                    sx={{
                      borderBottom: 영역6.length - 1 === i ? 'none' : '',
                    }}
                  >
                    <RadioGroup
                      sx={{
                        paddingTop: content.info.length > 1 ? '55px' : '28px',
                      }}
                      name={radioId6[content.id - 1]}
                      defaultValue={Number(getValues(radioId6[content.id - 1]))}
                    >
                      {content.desc.map((point, i) => {
                        if (point === '해당 없음 ') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no49'}
                                  value={i}
                                  onChange={handleChange6Lactation_none}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else if (point === '1회당 4점 :') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '43px',
                                textAlign: 'center',
                              }}
                            >
                              <Box>
                                <Radio
                                  disabled={disabled}
                                  name={'no49'}
                                  value={i}
                                  onChange={handleChange6Lactation_One}
                                />
                              </Box>
                            </TableRow>
                          );
                        }
                        return (
                          <TableRow
                            sx={{
                              lineHeight: '43px',
                              textAlign: 'center',
                            }}
                          >
                            <Box>
                              <Radio
                                disabled={disabled}
                                name={radioId6[content.id - 1]}
                                value={i}
                                onChange={handleChange6}
                              />
                            </Box>
                          </TableRow>
                        );
                      })}
                    </RadioGroup>
                  </StyledTableCellWithoutLeftTwo>
                  <StyledTableCellWithoutLeftRightTwo
                    sx={{
                      borderBottom: 영역6.length - 1 === i ? 'none' : '',
                    }}
                  >
                    <Box>
                      {content.info.includes('') ? (
                        <TableRow
                          sx={{
                            height: '16px',
                          }}
                        >
                          <Box sx={{ minWidth: '500px' }}> </Box>
                        </TableRow>
                      ) : content.info.length > 1 ? (
                        content.info.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '23px',
                                height: '4px',
                              }}
                            >
                              <Box
                                sx={{ minWidth: '65vw', marginLeft: '-28px' }}
                              >
                                ・ {item}
                              </Box>
                            </TableRow>
                          );
                        })
                      ) : (
                        content.info.map((item, i) => {
                          return (
                            <TableRow
                              sx={{
                                height: '8.5px',
                              }}
                            >
                              <Box
                                sx={{ minWidth: '65vw', marginLeft: '-28px' }}
                              >
                                ・ {item}
                              </Box>
                            </TableRow>
                          );
                        })
                      )}
                      {content.desc.map((v, i) => {
                        if (v === '1회당 4점 :') {
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '42px',
                              }}
                            >
                              <Box sx={{ minWidth: '500px' }}>
                                {v}
                                <Form.MuiTextField
                                  disabled={disableLactation ? true : disabled}
                                  required={false}
                                  type="number"
                                  textAlign="right"
                                  sx={{ width: '200px', marginLeft: '10px' }}
                                  InputProps={{
                                    ...Form.adornment('', '회'),
                                  }}
                                  {...register('no49_1', {
                                    onChange: e => {
                                      setValue('no49_1', e.target.value);
                                      setLactation(Number(e.target.value) * 4);
                                    },
                                  })}
                                />
                              </Box>
                            </TableRow>
                          );
                        } else
                          return (
                            <TableRow
                              sx={{
                                lineHeight: '42px',
                              }}
                            >
                              <Box sx={{ minWidth: '500px' }}>{v}</Box>
                            </TableRow>
                          );
                      })}
                    </Box>
                  </StyledTableCellWithoutLeftRightTwo>
                </TableRow>
              )
            )}
          </TableBody>
        </TableContainer>
      </Box>
    </>
  );
};

export default KPCSCContents3;
